// 확장자를 적는 이유: 이 파일과 아래 import 는 단위 테스트가 앱 번들 밖(node)에서 그대로 읽는다 —
// node ESM 은 상대 경로의 확장자를 추론하지 않는다.
import { accountInfoPathOf } from './adminRole.js'
import { hasAction } from './authActions.js'
import { isEmbedRoute } from './routeLayout.js'

/**
 * **이 세션이 이 화면을 여는가 — 라우터 가드의 판정 한 곳.** 의존성 없는 순수 모듈이다(Vue·스토어·`@/` 별칭 금지).
 *
 * 이 앱의 403 은 세션을 끝낸다(`plugins/http`). 그래서 **자기 메뉴에 없는 화면의 주소를 직접 열면** 그 화면이 첫 조회를
 * 부르는 순간 403 → 강제 로그아웃이다(R-K5 O-5: 내부 admin 이 `/v/business/store/list?storeCd=` → `GET /store` 403).
 * 화면을 띄운 뒤에는 막을 자리가 없다 — 조회는 화면마다 흩어져 있다. 그래서 **띄우기 전에** 세션이 가진 것으로 답한다:
 *
 * 1. 임베드 라우트(POS 안의 창)는 여기서 막지 않는다 — 그 화면이 같은 meta 를 읽어 페이지 안에서 안내한다.
 * 2. `meta.requiredAction` — 그 화면이 반드시 부르는 조회의 액션. 세션에 없으면 **액션 거절**.
 * 3. 로그인 전이면 막지 않는다 — `App.vue` 가 로그인 화면으로 보낸다(판정할 세션이 없다).
 * 4. `meta.menuFree` — 메뉴와 무관한 화면(로그인·역할 홈 분기·이 안내 화면, appserver 를 부르지 않는 화면).
 * 5. 역할이 정한 「내 계정 정보」 경로(`adminRole.accountInfoPathOf`) — 메뉴가 아니라 앱 바 버튼이 연다.
 * 6. 그 밖은 **세션 메뉴**(`GET /mainMenu` — 서버 `sys_auth_x_resources` 의 메뉴 리소스)가 증거다. 화면 경로가 메뉴에
 *    있으면 연다, 없으면 **메뉴 거절**. 메뉴 밖에서 들어오는 화면(하위 화면·바로가기)은 2 의 액션을 선언한다.
 *
 * 5·6 이 비교하는 「화면 경로」는 주소창의 문자열이 아니라 **매칭된 등록 라우트의 선언 경로**([registeredPathOf])다 —
 * vue-router 는 뒤 슬래시(`/v/business/store/list/`)·대소문자 변형을 같은 라우트로 매칭하는데, 문자열로 비교하면 메뉴에 있는
 * 화면이 그 표기로 왔을 때만 거절됐다(리뷰 X-b). 어느 등록 라우트에도 매칭되지 않은 주소는 여는 화면이 없으므로 열지 않는다.
 *    - 메뉴를 읽을 수 없는 세션(`MainMenu` 액션 없음)이거나 읽기에 실패했으면 증거가 없다 — 막지 않는다(종전 동작).
 *    - 아직 안 읽었으면 [ROUTE_ACCESS.NEED_MENU] — 경계는 셸을 그리고 화면 본문만 보류한다. 메뉴가 오면 다시 묻는다
 *      (`router`·`App.vue`, 읽기와 실패 기억은 `utils/sessionMenu`).
 *
 * 액션 판정은 `utils/authActions.hasAction` 한 곳이다(화면 버튼·조회 게이트 `permittedRead`·메뉴 조회와 같은 술어).
 */

/** 거절된 화면을 대신 여는 안내 화면 — 요청을 하나도 부르지 않는다 */
export const ACCESS_DENIED_PATH = '/v/denied'

/** 안내 화면의 문구 — i18n 키(한국어 문장 그대로) */
export const ACCESS_DENIED_TEXT = Object.freeze({
  title: '이 화면을 열 권한이 없습니다',
  hint: '이 계정의 메뉴·권한으로는 열 수 없는 화면입니다. 왼쪽 메뉴에서 화면을 고르세요.',
  home: '처음 화면으로',
})

/** 세션 메뉴를 부르는 액션 — `stores/menu.js` 가 같은 이름으로 막는다 */
export const MENU_ACTION = 'MainMenu'

/** 세션 메뉴의 상태 중 목록이 아닌 둘 */
export const MENU_STATE = Object.freeze({
  /** 아직 안 읽었다 — 읽고 다시 묻는다 */
  UNKNOWN: 'unknown',
  /** 읽으려 했지만 목록이 없다(실패 — 세션 동안 기억한다) — 증거가 없으니 막지 않는다 */
  UNAVAILABLE: 'unavailable',
})

/** 판정의 답 */
export const ROUTE_ACCESS = Object.freeze({
  ALLOW: Object.freeze({ allowed: true }),
  /** 세션에 그 화면의 액션이 없다 */
  REFUSE_ACTION: Object.freeze({ allowed: false, refusal: 'action' }),
  /** 세션 메뉴에 그 화면이 없다 */
  REFUSE_MENU: Object.freeze({ allowed: false, refusal: 'menu' }),
  /** 세션 메뉴를 읽어야 답할 수 있다 */
  NEED_MENU: Object.freeze({ allowed: false, needsMenu: true }),
})

/**
 * **로그인 없이 여는 화면인가** — 콜팝 가맹점 가입(`/join/callpop`)·휴대폰 서류 올리기(`/docs/upload`)(계획 callpop-web-signup §11.9).
 * 라우트 meta `public` 이 정본이고, `App.vue` 가 로그인 안 한 사람을 `/login` 으로 보내는 감시에서 이 화면을 뺀다. 공개 화면은 `menuFree` 도
 * 함께 선언한다 — 다른 계정으로 로그인해 둔 브라우저에서 열어도 세션 메뉴로 막지 않는다([routeAccess] 4). 순수.
 *
 * @param {?{meta?: object}} route
 * @returns {boolean}
 */
export function isPublicRoute(route) {
  return !!(route && route.meta && route.meta.public === true)
}

/** 서버 메뉴 목록(`/mainMenu` 행) → 화면 경로 목록. 순수 */
export function menuUrlsOf(menuList) {
  return Array.isArray(menuList) ? menuList.map((menu) => menu && menu.url).filter(Boolean) : []
}

/**
 * **그 라우트가 매칭된 등록 레코드의 선언 경로** — 주소 표기(뒤 슬래시·대소문자)와 무관한 화면의 이름이다. vue-router 의
 * `route.matched` 마지막 레코드(중첩이 없어 하나다)의 `path` 를 읽는다. 매칭된 레코드가 없으면 `null`. 순수.
 *
 * @param {{matched?: Array<{path: string}>}} route
 * @returns {?string}
 */
export function registeredPathOf(route) {
  const matched = route && Array.isArray(route.matched) ? route.matched : []
  const record = matched[matched.length - 1]

  return record && typeof record.path === 'string' ? record.path : null
}

/**
 * @param {{path: string, meta?: object, matched?: Array<{path: string}>}} route 가려는 라우트(vue-router 의 `Route`)
 * @param {{auth: ?object, authId: ?string, menu: (Array<string>|string)}} session
 *   - `auth` — 세션 스냅샷(`auth/auth`), 로그인 전이면 `sa_account` 가 `null`
 *   - `authId` — 첫 역할(로그인 전이면 `null`)
 *   - `menu` — 세션 메뉴의 화면 경로 목록([menuUrlsOf]) 또는 [MENU_STATE] 의 하나
 * @returns {{allowed: boolean, refusal?: string, needsMenu?: boolean}} [ROUTE_ACCESS] 의 하나
 */
export function routeAccess(route, session) {
  const meta = (route && route.meta) || {}
  const { auth, authId, menu } = session || {}

  if (isEmbedRoute(route)) return ROUTE_ACCESS.ALLOW
  if (meta.requiredAction) return hasAction(auth, meta.requiredAction) ? ROUTE_ACCESS.ALLOW : ROUTE_ACCESS.REFUSE_ACTION
  if (!auth || !auth.sa_account) return ROUTE_ACCESS.ALLOW
  if (meta.menuFree) return ROUTE_ACCESS.ALLOW

  const screen = registeredPathOf(route)
  if (screen !== null && screen === accountInfoPathOf(authId)) return ROUTE_ACCESS.ALLOW
  if (!hasAction(auth, MENU_ACTION)) return ROUTE_ACCESS.ALLOW
  if (menu === MENU_STATE.UNKNOWN) return ROUTE_ACCESS.NEED_MENU
  if (!Array.isArray(menu)) return ROUTE_ACCESS.ALLOW

  return screen !== null && menu.indexOf(screen) > -1 ? ROUTE_ACCESS.ALLOW : ROUTE_ACCESS.REFUSE_MENU
}

/**
 * 거절된 화면 대신 갈 곳 — `meta.fallbackPath` 가 있으면 그곳(문자열이거나 역할을 받는 함수 — 콜팝 라우트처럼 갈 곳이
 * 설계된 화면), 없으면 안내 화면([ACCESS_DENIED_PATH])이다. 안내 화면은 가려던 경로를 `from` 으로 받아 말한다(질의 문자열은
 * 싣지 않는다 — 매장 코드·전화번호가 실릴 수 있다). 순수.
 *
 * **언제나 `replace` 다**(리뷰 L-1). vue-router 3 은 가드의 `next(위치)` 를 `replace` 표지가 없으면 push 로 처리한다 — 권한 없는
 * 주소를 주소창·북마크·새 탭으로 열면 history 가 `[그 주소, 안내]` 가 되어, 뒤로 가기가 그 주소로 돌아갔다가 가드가 다시
 * 안내로 보낸다(이 앱 밖으로 뒤로 갈 수 없다). 거절된 주소는 history 에 남을 이유가 없다.
 *
 * @param {{path: string, meta?: object}} route
 * @param {?string} authId
 * @returns {{path: string, query?: {from: string}, replace: true}}
 */
export function refusedRouteTarget(route, authId) {
  const fallback = route && route.meta && route.meta.fallbackPath

  if (typeof fallback === 'function') return { path: fallback(authId) || '/', replace: true }
  if (fallback) return { path: fallback, replace: true }

  return { path: ACCESS_DENIED_PATH, query: { from: route.path }, replace: true }
}
